define(function(require) {
	var d3 = require('d3c');
	var moment = require('moment');

	var DATE_PICKER_FORMAT = 'YYYY-MM-DD-HH-mm-ss';
	var TIMESPAN_SEPARATOR = ' — ';

	/**
	 * @module utils/DateUtil
	 * Various date formatting and parsing functions.
	 */
	var DateUtil = {};

	/**
	 * Format a date with leading-zero day and abbreviated month
	 * @example '01 Jan' // en locale
	 * @example '01 Jän' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatShort = function(date) {
		return moment(date).format('DD MMM');
	};

	/**
	 * Format a date with leading-zero day, abbreviated month and full year.
	 * @example '01 Jan 1970' // en locale
	 * @example '01 Jän 1970' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatShortWithYear = function(date) {
		return moment(date).format('DD MMM YYYY');
	};

	/**
	 * Format a date with leading-zero day, abbreviated month,
	 * as well as the time with hours and minutes.
	 * @example '01 Jan 00:00' // en locale
	 * @example '01 Jän 00:00' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatShortWithTime = function(date) {
		return moment(date).format('DD MMM HH:mm');
	};

	/**
	 * Format a date with leading-zero day, abbreviated month, full year,
	 * as well as the time with hours and minutes.
	 * @example '01 Jan 1970 00:00' // en locale
	 * @example '01 Jän 1970 00:00' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatShortWithYearAndTime = function(date) {
		return moment(date).format('DD MMM YYYY HH:mm');
	};

	/**
	 * Format a date with abbreviated month
	 * @example 'Jan' // en locale
	 * @example 'Jän' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatMonth = function(date) {
		return moment(date).format('MMM');
	};

	/**
	 * Format a date with abbreviated month and full year
	 * @example 'Jan 2019' // en locale
	 * @example 'Jän 2019' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatMonthWithYear = function(date) {
		return moment(date).format('MMM YYYY');
	};

	/**
	 * Format a date with abbreviated month and abbreviated year
	 * @example "Jan '19" // en locale
	 * @example "Jän '19" // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatMonthWithYearShort = function(date) {
		return moment(date).format("MMM 'YY");
	};

	/**
	 * Format a date with full month
	 * @example 'January' // en locale
	 * @example 'Jänner' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatMonthFull = function(date) {
		return moment(date).format('MMMM');
	};

	/**
	 * Format a date with full month and full year
	 * @example 'January 2019' // en locale
	 * @example 'Jänner 2019' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatMonthFullWithYear = function(date) {
		return moment(date).format('MMMM YYYY');
	};

	/**
	 * Format a date with hours and minutes
	 * @example '12:34' // en locale
	 * @example '12:34' // de-at locale
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatTime = function(date) {
		return moment(date).format('HH:mm');
	};

	/**
	 * Format a date with day, month and the full year using ISO notation.
	 * @example '1970-01-01'
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatSearchDate = function(date) {
		return moment(date).format('YYYY-MM-DD');
	};

	/**
	 * Format a date with day, month and the full year using ISO notation and hours and minutes.
	 * @example '1970-01-01'
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatSearchDateWithTime = function(date) {
		return moment(date).format(DATE_PICKER_FORMAT);
	};

	/**
	 * Format a date with day, month and the full year and set time to midnight.
	 * @example '1970-01-01-00-00-00'
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatSearchDateBegin = function(date) {
		var d = new Date(date);
		d.setHours(0);
		d.setMinutes(0);
		d.setSeconds(0);
		return DateUtil.formatSearchDateWithTime(d);
	};

	/**
	 * Format a date with day, month and the full year and set time to midnight.
	 * @example '1970-01-01-00-00-00'
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatSearchDateEnd = function(date) {
		var d = new Date(date);
		d.setHours(23);
		d.setMinutes(59);
		d.setSeconds(59);
		return DateUtil.formatSearchDateWithTime(d);
	};

	/**
	 * Format a date usable with Direct Web Remoting, to circumvent timezone issues.
	 * @example '1970-1-1-0-0-0'
	 * @param {Date|String} date A native JS date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatDwrDateTime = function(date) {
		return moment(date).format('YYYY-M-D-H-m-s');
	};

	/**
	 * Parse a date with MomentJS.
	 * @see https://momentjs.com/docs/#/parsing/string/
	 * @param {String} string A parseable date string
	 */
	DateUtil.parseDate = function(string) {
		if (typeof string === 'object') return string;
		return moment(string).toDate();
	};

	/**
	 * Check whether a date string of the format 'YYYY-MM-DD' or 'YYYY-MM-DD-HH:mm:ss' contains time information.
	 * @param {String} str A parseable date string
	 * @returns {Boolean}
	 */
	DateUtil.dateStringContainsTime = function(str) {
		if (!str) return false;
		var date = moment(str, 'YYYY-MM-DD-HH:mm:ss');
		return date.isValid() && (str + '').length > 10;
	};

	/**
	 * Format a date string formatted as 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss' to a date string formatted as 'YYYY-MM-DD-HH-mm-ss'.
	 * @param {Date|String} str A date or parseable date string
	 * @returns {String}
	 */
	DateUtil.formatDatePickerDateTime = function(str) {
		var date = moment(str);

		if (!date.isValid()) console.warn('Invalid date format'); // eslint-disable-line no-console

		if (!this.dateStringContainsTime(str)) {
			// convert the date string into a date with the current time
			var now = moment();
			date
				.hour(now.hour())
				.minute(now.minute())
				.second(now.second());
		}

		return date.format(DATE_PICKER_FORMAT);
	};

	/**
	 * Convert a date string of the format 'YYYY-MM-DD' or 'YYYY-MM-DD-HH-mm-ss' to the format 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss'.
	 * @param {String} str A parseable date string
	 * @returns {String}
	 */
	DateUtil.convertFromDatePickerDateTime = function(str) {
		if (!this.dateStringContainsTime(str)) return str.substring(0, 10);
		return moment(str, DATE_PICKER_FORMAT).format('YYYY-MM-DD[T]HH:mm:ss');
	};

	/**
	 * Shift date by X days and return new date instance
	 * @param {Date} date date object
	 * @param {Number} deltaDays of days to shift date object
	 * @returns {Date} a new date object
	 */
	DateUtil.shiftDate = function(date, deltaDays) {
		return moment(date)
			.add(deltaDays, 'days')
			.toDate();
	};

	/**
	 * Shift date by X hours and return new date instance
	 * @param {Date} date date object
	 * @param {Number} deltaHours of hours to shift date object
	 * @returns {Date} a new date object
	 */
	DateUtil.shiftHour = function(date, deltaHours) {
		return moment(date)
			.add(deltaHours, 'hours')
			.toDate();
	};

	/**
	 * Shift date by X minutes and return new date instance
	 * @param {Date} date date object
	 * @param {Number} deltaMinutes of minutes to shift date object
	 * @returns {Date} a new date object
	 */
	DateUtil.shiftMinute = function(date, deltaMinutes) {
		return moment(date)
			.add(deltaMinutes, 'minutes')
			.toDate();
	};

	/**
	 * Shift date by X minutes and return new date instance
	 * @param {Date} date date object
	 * @param {Number} deltaMinutes of minutes to shift date object
	 * @returns {Date} a new date object
	 */
	DateUtil.shiftSeconds = function(date, deltaSeconds) {
		return moment(date)
			.add(deltaSeconds, 'seconds')
			.toDate();
	};

	/**
	 * Calculate difference between two days in requested precision
	 * @param {Date} firstDate date
	 * @param {Date} secondDate date
	 * @param {String} precision, one of 'years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'
	 */
	DateUtil.timeDiff = function(firstDate, secondDate, precision) {
		return moment(firstDate).diff(secondDate, precision);
	};

	/**
	 * Format date adaptive, depending on granularity, time span, ...
	 * @param {Date} beginDate date object
	 * @param {Date} endDate date object
	 * @param {String} granularity: 'HOUR', 'DAY'
	 */
	DateUtil.formatRangeAdaptive = function(beginDate, endDate, granularity) {
		var format = null;
		var from = null;
		var to = null;

		switch (granularity) {
			case 'WEEK':
			case 'DAY':
				if (
					beginDate.getFullYear() === endDate.getFullYear() &&
					beginDate.getMonth() === endDate.getMonth() &&
					beginDate.getDate() === endDate.getDate()
				) {
					// same year, month and day
					// 10 Oct
					format = '%d %b';
					from = d3.timeFormat(format)(beginDate);
				} else {
					if (beginDate.getFullYear() !== endDate.getFullYear()) {
						// different year
						// from 10 Oct 2017
						// to	10 Oct 2019
						format = '%d %b %Y';
						from = d3.timeFormat(format)(beginDate);
						to = d3.timeFormat(format)(endDate);
					} else {
						// same year
						// 10 Oct - 13 Nov
						format = '%d %b';
						from = d3.timeFormat(format)(beginDate) + TIMESPAN_SEPARATOR + d3.timeFormat(format)(endDate);
					}
				}
				break;

			default:
				// add time information
				format = '%d %b, %H:%M';
				var noTime = false;
				if (beginDate.getHours() === 0 && endDate.getHours() === 0 &&
					beginDate.getMinutes() === 0 && endDate.getMinutes() === 0 &&
					beginDate.getSeconds() === 0 && endDate.getSeconds() === 0) {
					// both set to migdnight - skip time
					noTime = true;
				}

				if (
					beginDate.getFullYear() === endDate.getFullYear() &&
					beginDate.getMonth() === endDate.getMonth() &&
					beginDate.getDate() === endDate.getDate()
				) {
					if (noTime) {
						// 10 Oct
						format = '%d %b';
						from = d3.timeFormat(format)(beginDate);
					} else {
						// same year, month and day
						// 10 Oct, 12:00 - 14:00
						from = d3.timeFormat(format)(beginDate);
						format = '%H:%M';
						from += TIMESPAN_SEPARATOR + d3.timeFormat(format)(endDate);
					}
				} else {
					if (noTime) {
						// 10 Oct - 12 Oct
						format = '%d %b';
						from = d3.timeFormat(format)(beginDate) + TIMESPAN_SEPARATOR + d3.timeFormat(format)(endDate);
					} else {
						// from 10 Oct, 12:00
						// to 12 Oct, 14:20
						from = d3.timeFormat(format)(beginDate);
						to = d3.timeFormat(format)(endDate);
					}
				}
		}

		if (to) {
			return from + TIMESPAN_SEPARATOR + to;
		} else {
			return from;
		}
	};

	/**
	 * Format two dates as a time span avoiding redundant output of month or year.
	 * @param {Date} from - The begin of the time span
	 * @param {Date} to - The end of the time span
	 * @param {Boolean} [returnParts] - True if formatted dates should be returned individually
	 * @return {String|Object} The formatted time span or an object containing the two formatted dates as properties
	 */
	DateUtil.formatTimeSpan = function(from, to, returnParts) {
		var formatted;
		var beginDate = moment(from);
		var endDate = moment(to);
		var redundancy = parseInt('00000', 2); // Each bit represents a datetime part (YMDHm); a bit will be set if both datetime parts are equal

		['m', 'H', 'D', 'M', 'Y'].forEach(function(format, index) {
			// Set up a bitmask for each datetime part, e.g. 0b10000 for the year, 0b01000 for the month etc.
			var bitmask = Math.pow(2, index);
			// Set the corresponding redundancy bit if both datetime parts are equal
			if (beginDate.format(format) === endDate.format(format)) redundancy |= bitmask;
		});

		// Now check the redundancy dimension and format the datetime parts accordingly
		if (redundancy < parseInt('10000', 2)) {
			// Not enough redundancy
			formatted = [DateUtil.formatShortWithYear(beginDate), DateUtil.formatShortWithYear(endDate)];
		} else if (redundancy < parseInt('11000', 2)) {
			// Redundant year
			formatted = [DateUtil.formatShort(beginDate), DateUtil.formatShortWithYear(endDate)];
		} else if (redundancy < parseInt('11100', 2)) {
			// Redundant year and month
			formatted = [beginDate.format('DD'), DateUtil.formatShortWithYear(endDate)];
		} else if (redundancy < parseInt('11111', 2)) {
			// Only hours and/or minutes differ
			formatted = [DateUtil.formatShortWithYearAndTime(beginDate), DateUtil.formatTime(endDate)];
		} else {
			// Same datetime (ignoring seconds)
			formatted = [DateUtil.formatShortWithYear(beginDate)];
		}

		if (returnParts) {
			return {
				from: formatted[0],
				to: formatted[1]
			};
		}

		return formatted[1] ? formatted[0] + TIMESPAN_SEPARATOR + formatted[1] : formatted[0];
	};

	/**
	 * Construct a minimal object containing all days of a specific month and year, divided in weeks.
	 * The resulting struct can be used to render a typical two-dimensional calendar view,
	 * e.g. a table showing the weeks in rows and the weekdays in columns.
	 * @param {number} [month] - The month the calendar should be generated from (e.g. January = 0; defaults to the current month)
	 * @param {number} [year] - The year of the month the calendar should be generated from (defaults to the current year)
	 * @param {Object} [options] - Options for configuring the output
	 * @param {number} [options.firstWeekday=1] - The day of the week to start the calendar with (defaults to Monday)
	 * @returns {CalendarMonth} The resulting calendar month
	 */
	DateUtil.getCalendarMonth = function(month, year, options = {}) {
		const firstWeekday = isNaN(options.firstWeekday) ? 1 : options.firstWeekday; // Monday by default
		const DAYS_PER_WEEK = 7;
		const now = new Date();

		// Setting the default month and year
		if (isNaN(month)) { month = now.getMonth(); }
		if (isNaN(year)) { year = now.getFullYear(); }

		const startDate = new Date(year, month, 1);
		const firstDay = (startDate.getDay() + (DAYS_PER_WEEK - firstWeekday)) % DAYS_PER_WEEK;
		const maxDays = DateUtil.getDaysInMonth(startDate);

		/**
		 * @typedef {Object} CalendarMonth
		 * @property {Date} startDate - The first date of this calendar month
		 * @property {number} numberOfDays - The number of days in this calendar month
		 * @property {Array[]} weeks - The list of weeks, each week containing the list of days (two-dimensional array)
		 * @property {number[]} weekdays - The list of weekdays, starting with the first weekday as set in options or by default
		 */
		const data = {
			startDate,
			numberOfDays: maxDays,
			weeks: [],
			weekdays: [...Array(DAYS_PER_WEEK).keys()].map(d => (d + firstWeekday) % DAYS_PER_WEEK)
		};

		let dayCounter = 1;

		out: for (let w = 0; w < 6; w += 1) {
			const week = [];

			for (let d = 0; d < 7; d += 1) {
				if (!data.weeks.length && d < firstDay) {
					week.push(null);
				} else if (dayCounter > maxDays) {
					if (!week.length) { break out; } //NOSONAR
					week.push(null);
				} else {
					week.push(dayCounter);
					dayCounter += 1;
				}
			}

			data.weeks.push(week);
		}

		return data;
	};

	/**
	 * Calculate the number of days in the month given by a speficic date.
	 * It is using a little trick to get this number: by setting the date to the zero-th day of the
	 * following month the date falls back to the last day of the current month. 🤪
	 * @param {Date} [date] - A date object set to the desired month or the current date by default
	 * @returns {number} - The resulting number of days (between 28 and 31, obv.)
	 */
	DateUtil.getDaysInMonth = function(date) {
		const clonedDate = date ? new Date(date.getFullYear(),date.getMonth()) : new Date();

		clonedDate.setMonth(clonedDate.getMonth() + 1);
		clonedDate.setDate(0);
		return clonedDate.getDate();
	};

	return DateUtil;
});
